'use es6';

import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import Env from 'enviro';
import { createStack } from '../index';
import { Promise } from '../helpers/promise';
import { set, setIf, setIn } from '../helpers/update';
import { handleResponse, responseError } from '../helpers/response';
import { parseUrl, buildUrl } from '../helpers/url';
import * as params from '../helpers/params';
import * as headers from '../helpers/headers'; // defensive over accessing localStorage because http://meyerweb.com/eric/thoughts/2012/04/25/firefox-failing-localstorage/

var getLocalStorage = function getLocalStorage() {
  try {
    return window.localStorage;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[hub-http] Exception accessing local storage.', error);
    return undefined;
  }
};

var getAppInfo = function getAppInfo() {
  return window.hubspot && window.hubspot.bender ? {
    name: window.hubspot.bender.currentProject,
    version: window.hubspot.bender.currentProjectVersion
  } : null;
};

export var services = function services(options) {
  return Object.assign({
    location: window.location,
    cookies: window.document.cookie,
    localStorage: getLocalStorage(),
    document: window.document,
    appInfo: getAppInfo()
  }, options);
};
export var validateOptions = function validateOptions(validator, errorMessage) {
  return function (options) {
    if (!validator || typeof validator !== 'function') {
      throw new Error('validator must be a function');
    }

    if (!validator(options)) throw new Error(errorMessage);
    return options;
  };
};
export var resolveApi = function resolveApi(api) {
  var environment = Env.getShort('hub-http');
  var location = environment === 'local' ? 'local' : 'deployed';
  var hostname = api[location] ? api[location][environment] : null;

  if (!hostname) {
    throw new Error("No hostname defined for environment " + environment + " and " + location);
  }

  return hostname;
};
var parsedUrl = Symbol('url'); // ensures consistant handling of the parsed url object when making url modifications
// without losing information

export var withUrl = function withUrl(urlMutator) {
  return function (options) {
    var descriptor = options[parsedUrl] || parseUrl(options.url);
    descriptor = urlMutator(descriptor);
    options = set(parsedUrl, descriptor)(options);
    options = set('url', buildUrl(descriptor))(options);
    return options;
  };
};
export var withOptions = function withOptions(options, newOptions) {
  return Object.assign({}, options, newOptions);
}; // TODO: REVIEW

var fromInput = function fromInput(propertyName, options) {
  /**
   * If options.input exists, this is a composed middleware's options, and we want to check if
   * the initial arguments had the property name. Otherwise, there's only one middleware
   * current options === initial options.
   */
  var input = options && options._input ? options._input : options;
  return input[propertyName] !== undefined ? input[propertyName] : undefined;
};

export var defaultTo = function defaultTo(propertyName, value) {
  return function (options) {
    return fromInput(propertyName, options) === undefined ? set(propertyName, value)(options) : options;
  };
};
export var method = function method(verb) {
  return defaultTo('method', verb);
};
export var header = function header(name, value, override) {
  return function (options) {
    var headerValue = headers.getHeader(name, options);
    return override || headerValue === undefined ? headers.setHeader(name, value, options) : options;
  };
};
export var base = function base(baseUrl) {
  return function (options) {
    return set('url', baseUrl + options.url)(options);
  };
};
var initialQuery = Symbol('initialQuery');
var notOverridableQuery = Symbol('noOverrideQuery');
var overridableQuery = Symbol('overrideQuery');
export var query = function query(obj) {
  var allowOverride = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  return withUrl(function (url) {
    var descriptor = url;

    if (typeof descriptor[initialQuery] === 'undefined') {
      descriptor = set(initialQuery, url.query || '')(descriptor);
    }

    var _ref = allowOverride ? [overridableQuery, descriptor[overridableQuery], obj] : [notOverridableQuery, obj, descriptor[notOverridableQuery]],
        _ref2 = _slicedToArray(_ref, 3),
        key = _ref2[0],
        baseObj = _ref2[1],
        superset = _ref2[2];

    descriptor = setIn([key], Object.assign({}, baseObj, {}, superset))(descriptor);
    return set('query', [descriptor[initialQuery], params.stringify(Object.assign({}, descriptor[overridableQuery], {}, descriptor[notOverridableQuery]))].filter(Boolean).join('&'))(descriptor);
  });
};
export var withQuery = function withQuery(options) {
  return query(options.query, false)(options);
};
export var httpsOnly = withUrl(set('protocol', 'https'));
export var environmentUrl = function environmentUrl(defaultApi) {
  return function (options) {
    return withUrl(function (url) {
      if (!url.protocol && options.location) {
        url.protocol = options.location.protocol.slice(0, -1);
      }

      if (!url.hostname) {
        var api = options.api || defaultApi;
        url.hostname = resolveApi(api);
      }

      return url;
    })(options);
  };
};
export var withApiAsOption = function withApiAsOption(options) {
  if (!options.api) {
    throw new Error('Missing api option. Expected api object (you can create one with the standardApi function');
  }

  return environmentUrl(null)(options);
};
export var standardApi = function standardApi(name, domainPrefix) {
  return {
    local: {
      qa: "local." + domainPrefix + "qa.com",
      prod: "local." + domainPrefix + ".com"
    },
    deployed: {
      qa: name + "." + domainPrefix + "qa.com",
      prod: name + "." + domainPrefix + ".com"
    }
  };
};
export var bodyType = function bodyType(contentType, stringifyFn) {
  return function (options) {
    options = header('content-type', contentType)(options);

    if (options.rawData) {
      options.data = options.rawData;
    } else if (typeof stringifyFn === 'function' && headers.getHeader('content-type', options) === contentType) {
      options.data = stringifyFn(options.data);
    }

    return options;
  };
};
export var jsonBody = function jsonBody(options) {
  return (// null will stringify to "null", whereas undefined will stringify to ""
    // (a body with content-length: 0), which is invalid json.
    options.data !== undefined || options.rawData !== undefined ? bodyType('application/json', JSON.stringify)(options) : options
  );
};

var wrapResponseHandler = function wrapResponseHandler(handler) {
  return function (response) {
    try {
      return handler(response);
    } catch (error) {
      error.response = response;
      throw error;
    }
  };
};

export var onResponse = function onResponse(handler) {
  return handleResponse(function (response) {
    return response.then(wrapResponseHandler(handler));
  });
};
export var onResponseError = function onResponseError(handler) {
  return handleResponse(function (response) {
    return response.catch(wrapResponseHandler(handler));
  });
};
export var responseInterceptor = function responseInterceptor(handler) {
  var alwaysRejectOnCatch = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  return handleResponse(function (response) {
    return response.then(wrapResponseHandler(handler), function (r) {
      return alwaysRejectOnCatch ? Promise.reject(handler(r)) : handler(r);
    });
  });
};

var getContentType = function getContentType(response) {
  if (!response || !response.headers) return '';
  return headers.getHeader('content-type', response) || '';
};

export var jsonResponse = createStack(header('Accept', 'application/json, text/javascript, */*; q=0.01'), onResponse(function (response) {
  return setIf(typeof response.data === 'string' && getContentType(response).indexOf('application/json') === 0, 'data', function () {
    return response.data.length ? JSON.parse(response.data) : undefined;
  })(response);
}));
export var reportOptionsError = onResponse(function (response) {
  if (response.errorCode === 'OPTIONSERROR') {
    return Promise.reject(responseError(response, "hub-http error building request options: " + response.options.error.message));
  }

  return response;
});
export var validateStatus = function validateStatus(options) {
  return onResponse(function (response) {
    return response.status >= 200 && response.status < 300 ? response : Promise.reject(responseError(response, "Request for " + options.url + " failed with status " + response.status + ". " + (response.statusText || '')));
  })(options);
};
export var retry = function retry(predicate) {
  var _ref3 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      reason = _ref3.reason,
      _ref3$maxRetries = _ref3.maxRetries,
      maxRetries = _ref3$maxRetries === void 0 ? 1 : _ref3$maxRetries,
      _ref3$delay = _ref3.delay,
      delay = _ref3$delay === void 0 ? 250 : _ref3$delay,
      onMaxAttemptsReached = _ref3.onMaxAttemptsReached;

  return function (options) {
    var interceptor = function interceptor(response) {
      if (predicate(response)) {
        var responseWithRetryInfo = set('retry', {
          reason: reason,
          maxRetries: maxRetries,
          delay: delay,
          exceededRetries: response.options.retryAttempts >= maxRetries
        })(response);
        return Promise.reject(responseWithRetryInfo);
      }

      return response;
    };

    if (maxRetries === 0) {
      // Make the retry middleware a no-op
      return options;
    } else {
      // Intercept the current response to do retries
      var responseMiddleware = options.retryAttempts >= maxRetries && typeof onMaxAttemptsReached === 'function' ? createStack(onResponse(interceptor), onMaxAttemptsReached) : onResponse(interceptor);
      return responseMiddleware(options);
    }
  };
};
export var safeMode = set('safeMode', true);